// CSV取り込みの確認ステップ (ファイル選択 → ★一覧で対象を選択★ → 一括登録画面)。
// irohana visa 一括同期 (BulkSyncLearnerModal) と同じ「一覧を見てチェックした行だけ取り込む」手順に揃える。
// 列はテンプレート (LEARNER_IMPORT_COLUMNS) の内容をそのまま確認できるよう全項目を出す。
// 取り込み前に直したくなるのはステータスのみ (他項目は一括登録画面で編集する) ため、ここではステータスだけ編集可にする。
// データは解析済み (最大 50 件) の手元データなので、並べ替え・ページングはクライアント側で行う。

import { FC, useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { MRT_ColumnDef, MRT_Localization } from "material-react-table";
import { Modal } from "@/components/elements/Modal";
import { Table } from "@/components/elements/Table";
import { Button } from "@/components/elements/Button";
import { ControlledPagination } from "@/components/elements/Pagination";
import { useSelectionTable } from "@/hooks/useSelectionTable";
import { IconDownloadBlue, IconFilterList, IconImportFile } from "@/assets/icons";
import { IconSort } from "@/assets/icons/IconSort";
import { SORTING, TSorting } from "@/constants/sorting";
import { EMPTY_STR } from "@/constants/string";
import { START_PAGE } from "@/constants/pagination";
import { background, black, text } from "@/theme/colors";
import { TOption } from "@/types/common";
import { MAX_IMPORT_LEARNERS, STATUS_OPTIONS, TBulkImportRow } from "@/features/learner/components/learner-bulk-import/bulk-import-mock";
import { LearnerPlanStatusSelect } from "@/features/learner/components/learner-list/LearnerPlanStatusSelect";
import { calculateResidencePeriods } from "@/features/learner/utils/calculates";
import { dayjs } from "@/utils/dayjs";
import { toast } from "@/utils/toast";

// 1ページの表示件数。取り込み上限 (50件) を1画面に詰め込まず、一覧と同じ感覚で確認させる。
const ROWS_PER_PAGE = 10;

// 1列のみのソート状態 (field = 下記 SORT_VALUE_BY_FIELD のキー)。null=未ソート。
type TSortState = { field: string; dir: TSorting } | null;

// クライアントソートの比較キー。表示文字列ではなく比較に適した値 (日付・数値) を返す。
const SORT_VALUE_BY_FIELD: Record<string, (_row: TBulkImportRow) => string | number> = {
  managementNumber: (row) => row.managementNumber ?? "",
  fullName: (row) => row.fullName,
  nickName: (row) => row.nickName ?? "",
  companyName: (row) => row.companyName,
  industryName: (row) => row.industryName ?? "",
  status: (row) => STATUS_OPTIONS.findIndex((opt) => opt.value === row.status),
  nationality: (row) => row.nationality,
  province: (row) => row.province ?? "",
  municipality: (row) => row.municipality ?? "",
  japaneseLevel: (row) => row.japaneseLevel,
  lastExamDate: (row) => toTimestamp(row.lastExamDate),
  lastScore: (row) => (row.lastScore === "" ? -1 : Number(row.lastScore)),
  // 1号残期間は表示が自動計算のため、比較は在留期限 (起算日 + 5年 + ブランク期間) の早い順で行う。
  remainingPeriod: (row) => {
    const start = dayjs(row.clause1StartDate);
    if (!row.clause1StartDate || !start.isValid()) return 0;
    return start
      .add(5, "year")
      .add(row.blankPeriodMonths ?? 0, "month")
      .valueOf();
  },
  clause1StartDate: (row) => toTimestamp(row.clause1StartDate),
  blankPeriodMonths: (row) => row.blankPeriodMonths ?? -1,
};

// 日付文字列 → 比較用のタイムスタンプ。空・不正値は 0 (最小) にして末尾/先頭へ寄せる。
function toTimestamp(value?: string | null): number {
  if (!value) return 0;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : 0;
}

// ソートUI。visa 一括同期の同名コンポーネントはサーバソート用のため、こちらはクライアントソート専用。
const SortHeader: FC<{ label: string; field?: string; sort: TSortState; onSort: (_field: string) => void }> = ({ label, field, sort, onSort }) => {
  const active = !!field && sort?.field === field;
  const isAsc = active && sort?.dir === SORTING.ASC;
  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={0.75}
      onClick={field ? () => onSort(field) : undefined}
      sx={{ cursor: field ? "pointer" : "default", userSelect: "none" }}
    >
      <span>{label}</span>
      {/* flexShrink:0 で狭い列でもアイコンが縮まないようにする。 */}
      {field &&
        (active ? (
          <IconSort style={{ flexShrink: 0, ...(isAsc ? { transform: "rotate(180deg)" } : {}) }} fontSize={18} />
        ) : (
          <IconFilterList style={{ flexShrink: 0 }} fontSize={18} color={black[80]} />
        ))}
    </Stack>
  );
};

type Props = {
  isOpen: boolean;
  rows: TBulkImportRow[];
  // 企業アカウントは所属機関を持たないため列を出さない (visa 一括同期と同じ)。
  isCompanyUser: boolean;
  // 日本語能力 enum (コード値 → N1..N5 のラベル変換に使う)。
  japaneseLevelOptions: TOption[];
  // 「CSV再取り込み」= 解析結果と選択中のファイルを破棄してファイル選択ステップへ戻す (別のファイルを入れ直す)。
  // ファイル選択ステップへ戻る手段はこのボタンのみ (DropZone は閉じると選択状態を失うため、
  // ファイルを保持したまま戻す「戻る」は表示と実状態がずれる)。
  onReimport: () => void;
  // 「キャンセル」「×」= 取り込み自体をやめる (ファイル・解析結果とも破棄してモーダルを閉じる)。
  onCancel: () => void;
  onImport: (selectedRows: TBulkImportRow[]) => void;
};

export const ImportLearnerFileConfirmModal: FC<Props> = ({ isOpen, rows, isCompanyUser, japaneseLevelOptions, onReimport, onCancel, onImport }) => {
  // ステータスの編集結果を保持する。取り込み時は編集後の値を一括登録画面へ渡す。
  const [editedRows, setEditedRows] = useState<TBulkImportRow[]>(rows);
  useEffect(() => setEditedRows(rows), [rows]);

  const [page, setPage] = useState(START_PAGE);
  const [sort, setSort] = useState<TSortState>(null);

  // ヘッダークリックでソートをトグル: 未ソート→ASC→DESC→未ソート。ソート変更時は1ページ目へ戻す。
  const handleSort = (field: string) => {
    setSort((prev) => {
      if (prev?.field !== field) return { field, dir: SORTING.ASC };
      if (prev.dir === SORTING.ASC) return { field, dir: SORTING.DESC };
      return null;
    });
    setPage(START_PAGE);
  };

  const handleChangeStatus = (id: number, status: string) => {
    setEditedRows((prev) => prev.map((row) => (row.id === id ? { ...row, status } : row)));
  };

  const sortedRows = useMemo(() => {
    if (!sort) return editedRows;
    const getValue = SORT_VALUE_BY_FIELD[sort.field];
    if (!getValue) return editedRows;
    const direction = sort.dir === SORTING.ASC ? 1 : -1;
    return [...editedRows].sort((a, b) => {
      const [va, vb] = [getValue(a), getValue(b)];
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * direction;
      return String(va).localeCompare(String(vb), "ja") * direction;
    });
  }, [editedRows, sort]);

  const totalPage = Math.max(1, Math.ceil(sortedRows.length / ROWS_PER_PAGE));
  const pageRows = useMemo(() => sortedRows.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE), [sortedRows, page]);

  const columns = useMemo<MRT_ColumnDef<TBulkImportRow>[]>(() => {
    const sortHeader = (label: string, field?: string) => <SortHeader label={label} field={field} sort={sort} onSort={handleSort} />;
    const cols: MRT_ColumnDef<TBulkImportRow>[] = [
      {
        header: "管理番号",
        id: "managementNumber",
        size: 110,
        Header: sortHeader("管理番号", "managementNumber"),
        Cell: ({ row }) => row.original.managementNumber || EMPTY_STR.TEXT,
      },
      { header: "氏名", id: "fullName", size: 170, Header: sortHeader("氏名", "fullName"), Cell: ({ row }) => row.original.fullName },
      {
        header: "呼び名",
        id: "nickName",
        size: 110,
        Header: sortHeader("呼び名", "nickName"),
        Cell: ({ row }) => row.original.nickName || EMPTY_STR.TEXT,
      },
      {
        header: "所属機関",
        id: "companyName",
        size: 180,
        Header: sortHeader("所属機関", "companyName"),
        // 列幅に収まらない場合は末尾を3点リーダーで省略する。
        // 既存に無い所属機関名は保存時に新規作成されるため、この時点で「(新規)」と明示する。
        Cell: ({ row }) => {
          const name = row.original.companyName;
          if (!name) return EMPTY_STR.TEXT;
          return (
            <Box component="span" sx={{ display: "block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {name}
              {row.original.isNewCompany ? "（新規）" : ""}
            </Box>
          );
        },
      },
      {
        header: "受験分野",
        id: "industryName",
        size: 120,
        Header: sortHeader("受験分野", "industryName"),
        // マスタに突合できなかった分野は空 (一括登録画面で選択させる)。
        Cell: ({ row }) => row.original.industryName || EMPTY_STR.TEXT,
      },
      {
        header: "ステータス",
        id: "status",
        size: 134,
        Header: sortHeader("ステータス", "status"),
        Cell: ({ row }) => (
          <LearnerPlanStatusSelect
            value={row.original.status}
            options={STATUS_OPTIONS}
            onChange={(value) => handleChangeStatus(row.original.id, value)}
          />
        ),
      },
      { header: "国籍", id: "nationality", size: 120, Header: sortHeader("国籍", "nationality"), Cell: ({ row }) => row.original.nationality },
      // 性別はソート対象外 (表示のみ)。
      { header: "性別", id: "gender", size: 80, Header: sortHeader("性別"), Cell: ({ row }) => row.original.gender || EMPTY_STR.TEXT },
      {
        header: "居住地（都道府県）",
        id: "province",
        size: 150,
        Header: sortHeader("居住地（都道府県）", "province"),
        Cell: ({ row }) => row.original.province || EMPTY_STR.TEXT,
      },
      {
        header: "居住地（市区町村）",
        id: "municipality",
        size: 150,
        Header: sortHeader("居住地（市区町村）", "municipality"),
        Cell: ({ row }) => row.original.municipality || EMPTY_STR.TEXT,
      },
      {
        header: "日本語能力",
        id: "japaneseLevel",
        size: 110,
        Header: sortHeader("日本語能力", "japaneseLevel"),
        // 保持しているのは enum のコード値 (n3) なのでラベル (N3) に変換して表示する。
        // 突合できない値は保持していない (そのまま保存すると BE へ不正なコード値が飛ぶため空にしている)。
        Cell: ({ row }) => japaneseLevelOptions.find((opt) => String(opt.value) === row.original.japaneseLevel)?.label || EMPTY_STR.TEXT,
      },
      {
        header: "前回試験の受験日",
        id: "lastExamDate",
        size: 150,
        Header: sortHeader("前回試験の受験日", "lastExamDate"),
        Cell: ({ row }) => row.original.lastExamDate || EMPTY_STR.TEXT,
      },
      {
        header: "前回点数",
        id: "lastScore",
        size: 110,
        Header: sortHeader("前回点数", "lastScore"),
        Cell: ({ row }) => (row.original.lastScore ? `${row.original.lastScore}点` : EMPTY_STR.TEXT),
      },
      {
        header: "1号残期間",
        id: "remainingPeriod",
        size: 110,
        Header: sortHeader("1号残期間", "remainingPeriod"),
        // 1号の起算日 + ブランク期間 から自動計算する (一括登録画面と同じ計算)。
        Cell: ({ row }) => calculateResidencePeriods(row.original.clause1StartDate || null, row.original.blankPeriodMonths, EMPTY_STR.TEXT).remaining,
      },
      {
        header: "1号の起算日",
        id: "clause1StartDate",
        size: 120,
        Header: sortHeader("1号の起算日", "clause1StartDate"),
        Cell: ({ row }) => row.original.clause1StartDate || EMPTY_STR.TEXT,
      },
      {
        header: "ブランク期間（月単位）",
        id: "blankPeriodMonths",
        size: 180,
        Header: sortHeader("ブランク期間（月単位）", "blankPeriodMonths"),
        Cell: ({ row }) => (row.original.blankPeriodMonths != null ? `${row.original.blankPeriodMonths}か月` : EMPTY_STR.TEXT),
      },
    ];
    // 会社アカウントでは所属機関の列を除外する
    return cols.filter((col) => !(isCompanyUser && col.id === "companyName"));
  }, [isCompanyUser, japaneseLevelOptions, sort]);

  // 解析できた行はすべて取り込み候補のため、初期状態は全行を選択済みにする
  // (このモーダルは rows が確定したときだけマウントされるため、初期値のみで足りる)。
  const defaultSelectionKeys = useMemo(() => rows.map((row) => String(row.id)), [rows]);

  // 上限 (50件) を超える選択を試みたときだけ出す注意書き。
  // 常時表示だと何も操作していないのに赤字が出ている状態になり、上限に当たったことが伝わらないため。
  const [isExceedMaxSelection, setIsExceedMaxSelection] = useState(false);

  const { table, rowSelectionKeys } = useSelectionTable({
    primaryKey: "id",
    columns,
    rows: pageRows,
    defaultSelectionKeys,
    enableRowSelection: true,
    // grid-no-grow: 列幅を size 固定にする。CSV の全項目を出すため合計幅がモーダル幅を超え、
    // grid だと各列が潰れてしまう (超過分は横スクロールで見せる)。
    layoutMode: "grid-no-grow",
    pinningColumnsLeft: ["mrt-row-select"],
    maxSelection: MAX_IMPORT_LEARNERS,
    onExceedMaxSelection: (max) => {
      setIsExceedMaxSelection(true);
      toast.error(`一度に取り込める外国人は${max}名までです。${max}名以内で選択してください。`);
    },
    localization: {
      toggleSelectAll: "すべて選択",
      toggleSelectRow: "選択",
    } as Partial<MRT_Localization> as MRT_Localization,
  });

  // 選択を減らして上限内に戻ったら注意書きを消す (上限に当たっている間だけ出す)。
  useEffect(() => {
    if (rowSelectionKeys.length < MAX_IMPORT_LEARNERS) setIsExceedMaxSelection(false);
  }, [rowSelectionKeys.length]);

  const handleImport = () => {
    const selectedKeys = new Set(rowSelectionKeys);
    // 選択はページを跨いで保持されるため、現在ページではなく全行から取り出す (ステータスは編集後の値)。
    onImport(editedRows.filter((row) => selectedKeys.has(String(row.id))));
  };

  return (
    <Modal isOpen={isOpen} title="CSVから外国人を取り込み" width={1134} enableFooter={false} onClose={onCancel}>
      <Stack gap={2} height="100%" mt={1}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexShrink={0}>
          <Typography variant="body14Semi" color={text.primary}>
            全 {editedRows.length.toLocaleString("en-US")} 件中 {pageRows.length} 件を表示
          </Typography>
          <Stack direction="row" alignItems="center" gap={2}>
            {isExceedMaxSelection && (
              <Typography variant="cap12" color={text.error}>
                CSV取込は一度に{MAX_IMPORT_LEARNERS}件です
              </Typography>
            )}
            {/* 一覧を見て「ファイルが違う / 直して出し直す」となった場合に、ファイル選択からやり直せるようにする。 */}
            <Button variant="outline" size="md" onClick={onReimport} startIcon={<IconImportFile width={16} height={19} />}>
              <Typography variant="body14Semi">CSV再取り込み</Typography>
            </Button>
          </Stack>
        </Stack>

        <Stack
          minHeight={0}
          sx={{
            "& > div": { backgroundColor: background.white },
            "& .MuiPaper-root": { backgroundColor: background.white },
          }}
        >
          <Table table={table} minHeight={150} maxHeight="calc(90vh - 260px)" />
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="space-between" flexShrink={0} mt={1}>
          {totalPage > 1 ? <ControlledPagination totalPage={totalPage} page={page} onChangePage={setPage} /> : <span />}
          <Stack direction="row" gap={2}>
            <Button variant="ghost" size="md" onClick={onCancel}>
              <Typography variant="body14Semi">キャンセル</Typography>
            </Button>
            <Button
              size="md"
              disabled={rowSelectionKeys.length === 0}
              onClick={handleImport}
              startIcon={<IconDownloadBlue width={20} height={20} color={text.white} />}
            >
              <Typography variant="body14Semi">チェックした項目を取り込む</Typography>
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Modal>
  );
};
